"use client";

import { Mountain, Coffee, Route, Satellite, Landmark, Tent } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BenefitsOfLandClearing() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const benefits = [{
    index: "B.01",
    icon: Mountain,
    title: "Eco-Friendly Forestry Mulching",
    body: "Our specialized mulching heads turn brush and trees into nutrient-rich ground cover. This process prevents soil erosion and eliminates the need for burning or hauling debris."
  }, {
    index: "B.02",
    icon: Coffee,
    title: "Immediate Land Accessibility",
    body: "Unlike traditional clearing, our method leaves a smooth, walkable surface. You can enjoy your reclaimed property the same day we finish, with no stumps or holes left behind."
  }, {
    index: "B.03",
    icon: Route,
    title: "Precision Underbrush Removal",
    body: "We selectively clear invasive species and thickets while preserving your valuable hardwoods. It's about enhancing the natural beauty and health of your existing forest."
  }, {
    index: "B.04",
    icon: Satellite,
    title: "Advanced Site Mapping",
    body: "We use GPS-guided equipment to ensure exact property line adherence and sensitive area protection. We clear exactly what you want, where you want it, every time."
  }, {
    index: "B.05",
    icon: Landmark,
    title: "Local Land Stewardship",
    body: "As a local operator, we understand the specific soil types and vegetation of our region. We treat every acre with the respect a neighbor's property deserves."
  }, {
    index: "B.06",
    icon: Tent,
    title: "Heavy-Duty Industrial Fleet",
    body: "Our high-flow hydraulic machines are built for the toughest terrain. We maintain our own equipment to ensure zero downtime and maximum efficiency on your project."
  }];
  return <section data-section-id="1437" id="benefits-of-land-clearing" className="relative bg-background text-foreground py-32 md:py-44 overflow-hidden">
      {}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-secondary/5 border-t border-secondary/10 overflow-hidden flex items-center">
        <div className="flex whitespace-nowrap animate-[ticker_40s_linear_infinite]">
          {[0, 1].map(dup => <div key={dup} data-index={dup} className="flex shrink-0">
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground/50 px-8">SITE PREP</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground/50 px-8">BRUSH CONTROL</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground/50 px-8">FIRE MITIGATION</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground/50 px-8">TRAIL CLEARING</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground/50 px-8">PASTURE RECLAMATION</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground/50 px-8">SURVEY LINES</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground/50 px-8">VIEW ENHANCEMENT</span>
              <span className="font-mono text-xs text-primary px-2">·</span>
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={headRef} className={`lg:sticky lg:top-32 relative transition-all duration-[700ms] ease-out ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
              {}
              <div className="pointer-events-none select-none absolute -top-8 -left-2 font-serif italic font-light text-primary/[0.08] leading-none" style={{
              fontSize: "clamp(8rem, 15vw, 15rem)"
            }} aria-hidden="true">
                02
              </div>

              {}
              <p className="relative font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground mb-10">
                § 02 · PRECISION MULCHING — WHY WE CLEAR
              </p>

              {}
              <div className="relative flex items-center mb-10">
                <div className="h-px w-20 bg-primary" />
                <div className="w-1.5 h-1.5 bg-primary rounded-lg" />
              </div>

              {}
              <h2 className="relative font-default font-bold text-foreground leading-[0.96] tracking-tight mb-10" style={{
              fontSize: "clamp(2.5rem, 5.5vw, 5rem)"
            }}>
                Why owners{" "}
                <span className="italic text-primary font-light">trust us.</span>
              </h2>

              {}
              <p className="relative max-w-[320px] text-muted-foreground leading-[1.55] mb-12" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)"
            }}>
                Over ninety percent of our clients recommend Chipper Mulching Co. to their neighbors. We don&apos;t just clear land—we transform overgrown properties into usable, beautiful assets while respecting the local ecosystem.
              </p>

              {}
              <Link to="/ServicesPage" className="relative inline-block font-default text-sm tracking-wider uppercase text-foreground font-semibold underline decoration-primary decoration-2 underline-offset-[6px] hover:underline-offset-[10px] transition-all duration-200 ease-out">
                Explore Our Services
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <ul className="flex flex-col">
              {benefits.map((benefit, i) => {
              const Icon = benefit.icon;
              return <BenefitRow key={i} i={i} benefit={benefit} Icon={Icon} isLast={i === benefits.length - 1} />;
            })}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}
function BenefitRow({
  i,
  benefit,
  Icon,
  isLast
}: {
  i: number;
  benefit: {
    index: string;
    title: string;
    body: string;
  };
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>({
    threshold: 0.15
  });
  return <li ref={ref} data-index={i} className={`group relative grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 py-10 md:py-12 transition-all duration-[700ms] ${i !== 0 ? "border-t border-border" : ""} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    transitionDelay: `${i * 90}ms`
  }}>
      {}
      <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-4 md:gap-6">
        <span className="font-mono text-xs tracking-widest uppercase text-primary font-bold">
          {benefit.index}
        </span>
        <div className="relative w-6 h-6 flex items-center justify-center">
          <Icon className="w-6 h-6 text-secondary transition-all duration-[260ms] ease-out group-hover:text-primary" strokeWidth={1.5} />
        </div>
      </div>

      {}
      <div className="md:col-span-10">
        <h3 className="font-default font-semibold text-foreground leading-[1.15] tracking-tight mb-5 transition-transform duration-[260ms] ease-out group-hover:-translate-y-1" style={{
        fontSize: "clamp(1.375rem, 1.8vw, 1.75rem)"
      }}>
          {benefit.title}
        </h3>
        <p className="max-w-[480px] text-muted-foreground leading-[1.6] mb-6" style={{
        fontSize: "clamp(0.9375rem, 1vw, 1rem)"
      }}>
          {benefit.body}
        </p>

        {}
        {!isLast && <div className="flex items-center">
            <div className="h-px w-20 bg-primary/30" />
            <div className="w-1.5 h-1.5 bg-primary/30 rounded-lg" />
          </div>}
      </div>
    </li>;
}